import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PlayCircle, OctagonX } from "lucide-react";

export default function PlanosAcao() {
  const { user } = useAuth();
  const [filtro, setFiltro] = useState("ativo");
  const [lista, setLista] = useState([]);
  const [cessar, setCessar] = useState(null);
  const [motivo, setMotivo] = useState("");

  const load = async () => {
    const r = await api.get("/admin/planos-acao", { params: { status: filtro || undefined } });
    setLista(r.data);
  };
  useEffect(() => { load(); }, [filtro]);

  const dispararCron = async () => {
    try {
      await api.post("/admin/cron/planos-acao/executar");
      toast.success("Job executado · veja novos planos");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const enviarCessacao = async () => {
    if (!motivo.trim()) { toast.error("Informe motivo"); return; }
    try {
      await api.post(`/admin/planos-acao/${cessar.id}/cessar`, { motivo });
      toast.success("Plano cessado");
      setCessar(null); setMotivo("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Operação · Plano de Ação</div>
          <h1 className="font-display font-extrabold text-3xl text-aj-blue-deep mt-1">Planos de Ação Persistentes</h1>
          <p className="text-sm text-aj-text-muted mt-1 max-w-2xl">
            Empresas que exigem CNPJ ativo geram déficit em regiões com volumetria maior que oferta.
            Cobrança automática à montadora a cada 2 dias até captação ou cessação por Admin.
          </p>
        </div>
        {user?.role === "admin" && (
          <button onClick={dispararCron} className="btn-aj-outline text-sm" data-testid="pa-cron">
            <PlayCircle size={16} /> Executar verificação agora
          </button>
        )}
      </div>

      <div className="flex gap-3 items-end">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted mb-1.5">Filtro</div>
          <Select value={filtro} onValueChange={setFiltro}>
            <SelectTrigger className="w-[180px] rounded-lg" data-testid="pa-filtro"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ativo">Ativos</SelectItem>
              <SelectItem value="resolvido">Resolvidos</SelectItem>
              <SelectItem value="cessado">Cessados</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="aj-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-aj-surface-2 border-b border-[#E2E8F0]">
            <tr>
              <Th>Empresa</Th>
              <Th>Região</Th>
              <Th>Montadora</Th>
              <Th>Déficit</Th>
              <Th>Disparos</Th>
              <Th>Último disparo</Th>
              <Th>Status</Th>
              {user?.role === "admin" && <Th>Ações</Th>}
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 && <tr><td colSpan={8} className="p-12 text-center text-aj-text-muted">Nenhum plano</td></tr>}
            {lista.map(p => (
              <tr key={p.id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-aj-surface transition-colors" data-testid={`pa-${p.id}`}>
                <td className="p-3 font-semibold text-aj-text">{p.empresa_nome}</td>
                <td className="p-3 text-xs">{p.cidade}/{p.estado}</td>
                <td className="p-3 text-xs">{p.montadora_nome || "—"}</td>
                <td className="p-3 font-mono text-aj-danger font-bold">{p.deficit}</td>
                <td className="p-3 font-mono">{p.disparos}</td>
                <td className="p-3 text-xs">{p.ultimo_disparo ? new Date(p.ultimo_disparo).toLocaleString("pt-BR") : "—"}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${
                    p.status === "ativo" ? "bg-aj-amber-soft text-aj-orange-hover"
                    : p.status === "resolvido" ? "bg-aj-success text-white"
                    : "bg-aj-surface-2 text-aj-text-muted"
                  }`}>{p.status}</span>
                </td>
                {user?.role === "admin" && (
                  <td className="p-3">
                    {p.status === "ativo" && (
                      <Button size="sm" variant="outline"
                        className="rounded-lg h-7 text-[10px] border-aj-danger text-aj-danger hover:bg-aj-danger hover:text-white"
                        onClick={() => setCessar(p)} data-testid={`pa-cessar-${p.id}`}>
                        <OctagonX size={12} /> Cessar
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!cessar} onOpenChange={(o) => !o && setCessar(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Cessar Plano de Ação</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">
              <strong>{cessar?.empresa_nome}</strong> · {cessar?.cidade}/{cessar?.estado}
            </div>
            <Textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)}
              className="rounded-lg"
              placeholder="Motivo da cessação (registrado no log)" data-testid="pa-motivo" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setCessar(null)}>Cancelar</Button>
            <Button onClick={enviarCessacao} className="rounded-lg bg-aj-danger hover:bg-red-600 text-white" data-testid="pa-confirmar">
              Confirmar cessação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">{children}</th>;
}
